/**
 * O bloco de devolução de estoque DENTRO do diálogo de exclusão.
 *
 * Três diálogos usam este mesmo bloco (orçamento, exclusão em massa e ordem de
 * serviço). Ele é o único lugar onde o usuário pode dizer "não devolva" — a
 * exclusão apaga o vínculo dos movimentos, então não há convite depois.
 */
import { Checkbox } from '@/components/ui/checkbox';
import { Undo2 } from 'lucide-react';
import { plural } from '@/lib/plural';
import type { DocumentDeletionReturn } from '@/hooks/useDocumentDeletionReturn';

export function StockReturnOnDeleteNotice({ state }: { state: DocumentDeletionReturn }) {
  if (state.action === 'off' || state.productCount === 0) return null;

  // O sufixo acompanha a CONTAGEM DE DOCUMENTOS, não a de produtos: na exclusão
  // em massa o aviso dizia "neste documento" com 3 orçamentos selecionados
  // (achado em device). E a contagem é a de documentos COM baixa, não a de
  // selecionados — prometer devolução de um orçamento que nunca baixou nada
  // seria mentira, e é justamente o caso que o teste ao vivo cobriu.
  const onde =
    state.documentCount <= 1
      ? 'neste documento'
      : `em ${plural(state.documentCount, 'documento', 'documentos')}`;
  const label = `${plural(state.productCount, 'produto baixado', 'produtos baixados')} ${onde}`;

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 dark:border-sky-900/50 dark:bg-sky-950/20">
      <div className="flex items-start gap-3">
        <Undo2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
        {state.action === 'auto' ? (
          <p className="text-sm">
            {label} {state.productCount === 1 ? 'voltará' : 'voltarão'} ao estoque automaticamente.
          </p>
        ) : (
          <>
            {/* Checkbox + área de texto irmã com onClick próprio: `<label>` em
                volta do Checkbox do Radix alterna duas vezes (CLAUDE.md #29). */}
            <Checkbox
              id="return-stock-on-delete"
              checked={state.shouldReturn}
              onCheckedChange={(checked) => state.setShouldReturn(checked === true)}
              onClick={(e) => e.stopPropagation()}
            />
            <div
              className="cursor-pointer text-sm"
              onClick={() => state.setShouldReturn(!state.shouldReturn)}
            >
              Devolver ao estoque ({label})
            </div>
          </>
        )}
      </div>
    </div>
  );
}
